import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { useCreateContact } from '@/hooks/useApi'
import { ApiError } from '@/lib/api-error'
import type { ContactCreatePayload } from '@/types/api'

const EMPTY_FORM = { email: '', first_name: '', last_name: '', company: '' }

type ContactForm = typeof EMPTY_FORM

/** Mirrors the backend rule: a local part, a domain and a dot in it, no spaces. */
function isValidEmail(value: string): boolean {
  return /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/.test(value.trim())
}

/** Drop the optional fields the user left blank so the API stores null, not "". */
function toPayload(form: ContactForm): ContactCreatePayload {
  const payload: ContactCreatePayload = { email: form.email.trim() }
  if (form.first_name.trim()) payload.first_name = form.first_name.trim()
  if (form.last_name.trim()) payload.last_name = form.last_name.trim()
  if (form.company.trim()) payload.company = form.company.trim()
  return payload
}

/** Localize the one refusal we can name; anything else is shown as the API worded it. */
function useRefusalMessage() {
  const { t } = useTranslation('contacts')
  return (error: Error) =>
    error instanceof ApiError && error.status === 409
      ? t('createDialog.duplicate')
      : error.message
}

export function ContactCreateDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('contacts')
  const { toast } = useToast()
  const createContact = useCreateContact()
  const refusalMessage = useRefusalMessage()
  const [form, setForm] = useState<ContactForm>(EMPTY_FORM)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setForm(EMPTY_FORM)
      setError(null)
    }
  }, [open])

  const update = (field: keyof ContactForm) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((current) => ({ ...current, [field]: event.target.value }))
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (!isValidEmail(form.email)) {
      setError(t('createDialog.invalidEmail'))
      return
    }
    setError(null)
    createContact.mutate(toPayload(form), {
      onSuccess: () => {
        toast({ title: t('createDialog.created') })
        onOpenChange(false)
      },
      onError: (mutationError: Error) => setError(refusalMessage(mutationError)),
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('createDialog.title')}</DialogTitle>
          <DialogDescription>{t('createDialog.description')}</DialogDescription>
        </DialogHeader>

        {/* noValidate: the form reports the invalid address itself, in the user's language. */}
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="contact-email">{t('createDialog.email')}</Label>
            <Input
              id="contact-email"
              type="email"
              autoComplete="off"
              placeholder={t('createDialog.emailPlaceholder')}
              value={form.email}
              onChange={update('email')}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contact-first-name">{t('createDialog.firstName')}</Label>
              <Input id="contact-first-name" value={form.first_name} onChange={update('first_name')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-last-name">{t('createDialog.lastName')}</Label>
              <Input id="contact-last-name" value={form.last_name} onChange={update('last_name')} />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-company">{t('createDialog.company')}</Label>
            <Input id="contact-company" value={form.company} onChange={update('company')} />
          </div>

          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('createDialog.cancel')}
            </Button>
            <Button type="submit" disabled={createContact.isPending}>
              {createContact.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {createContact.isPending ? t('createDialog.submitting') : t('createDialog.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
